import React, { useMemo, useState } from "react";
import { Serie, SerieLayoutStyle } from "../../../types/Serie";
import { NextPage, NextPageContext } from "next";
import { notAuthorised } from "../../../request";
import Header from "../../../components/Header";
import { getCommonData } from "../../../helpers/common-data";
import { CommonData } from "../../../types/CommonData";
import Footer from "../../../components/Footer";
import { InPageMenu } from "../../../components/InPageMenu";
import { MasonryGrid } from "../../../components/MasonryGrid";
import { ProductThumb } from "../../../components/ProductThumb";
import { Wrapper } from "../../../components/Wrapper";

import { getAnnouncements } from "../../../helpers/getAnnouncements";
import { Announcement } from "../../../types/Announcement";
import { layouts } from "../../../helpers/filters";
import { InfiniteProducts } from "../../../components/InfiniteProducts";
import { ProductBigThumb } from "../../../components/ProductBigThumb";
import { useResponsive } from "../../../context/ResponsiveContext";
import { useChatbox } from "../../../helpers/useChatbox";
import { getRichSnippet } from "../../../helpers/rich-snippets/series";
import { getFrontendUrl } from "../../../helpers/utils";
import { SEOTags } from "../../../components/SEOTags";
import Error from "../../_error";
import { Product } from "../../../types/Product";
import { QuickShopProductModal } from "../../../modals/QuickProductModal";

export const SerieShop: NextPage<{
  data: Serie | null;
  commonData: CommonData;
  announcements: Announcement[];
}> = ({ data, commonData, announcements }) => {
  const [layout, setLayout] = useState<SerieLayoutStyle>(
    data?.layout_style || "masonry"
  );
  const { isMobile, isTablet, is13inch } = useResponsive();
  const currentLayout = layouts.find((l) => l.name === layout);
  const [previewProduct, setPreviewProduct] = useState<Product | undefined>();

  const handleProductPreview = (product: Product) => {
    setPreviewProduct(product);
  };

  useChatbox();

  const columns = useMemo(() => {
    if (isMobile) {
      return 1;
    }

    if (isTablet || is13inch || layout === "wide") {
      return 2;
    }

    if (layout === "masonry") {
      return 3;
    }

    return 2;
  }, [is13inch, isMobile, isTablet, layout]);

  if (!data) {
    return (
      <Error
        commonData={commonData}
        announcements={announcements}
        statusCode={404}
      />
    );
  }

  return (
    <>
      <SEOTags
        title={data.name}
        description={data.description}
        keywords={data.keywords}
        url={getFrontendUrl(`/series/${data.slug}/shop`)}
        featuredImage={data.featured_image}
        richSnippets={getRichSnippet(data)}
      />
      <Header headerStyle={"DEFAULT"} announcements={announcements} />
      <InPageMenu menu={data.menu} name={data.name} notSticky={isMobile} />
      <InfiniteProducts
        tags={data.tags}
        serie={data.slug}
        skipPairings
        currentLayout={currentLayout}
        onLayoutChange={(layout) => {
          setLayout(layout.name);
        }}
        stickyOffset={93}
        isSticky
        urlFilters
        filtersInWrapper
      >
        {(results) => {
          return (
            <Wrapper type="header">
              <MasonryGrid
                columns={columns}
                margin={layout === "masonry" ? 2 : 4}
                items={results}
              >
                {(product, index) => {
                  let isFullWidth = false;
                  if (product.featured_image) {
                    const aspectRatio =
                      product.featured_image.width /
                      product.featured_image.height;
                    isFullWidth = aspectRatio > 2;
                  }

                  if (layout === "masonry") {
                    return (
                      <div
                        className={`grid-item`}
                        style={{
                          marginBottom: isMobile ? "5%" : "2%",
                        }}
                        key={`masonry-item--${index}`}
                      >
                        <ProductThumb
                          key={product.id}
                          product={product}
                          enableOverlay
                          onQuickShop={() => handleProductPreview(product)}
                        />
                      </div>
                    );
                  }

                  if (layout === "big") {
                    return (
                      <ProductBigThumb
                        key={product.id}
                        product={product}
                        margin={4}
                      />
                    );
                  }

                  if (layout === "wide") {
                    return (
                      <div
                        className={`grid-item ${
                          isFullWidth ? "grid-item--fullwidth" : ""
                        }`}
                        style={{
                          marginBottom: isMobile ? "5%" : "2%",
                        }}
                        key={`masonry-item--${index}`}
                      >
                        <ProductThumb
                          key={product.id}
                          product={product}
                          enableOverlay
                          onQuickShop={() => handleProductPreview(product)}
                          previewWidth={columns === 2 ? "large" : undefined}
                        />
                      </div>
                    );
                  }

                  return null;
                }}
              </MasonryGrid>
            </Wrapper>
          );
        }}
      </InfiniteProducts>
      <QuickShopProductModal
        visible={!!previewProduct}
        onClose={() => setPreviewProduct(undefined)}
        product={previewProduct}
      />
      <Footer />
    </>
  );
};

interface Context extends NextPageContext {
  query: {
    slug: string;
  };
}

SerieShop.getInitialProps = async (ctx: Context) => {
  let data = null;

  try {
    const response = await notAuthorised.request<Serie>({
      method: "GET",
      url: `/api/frontend/series/${ctx.query.slug}/`,
    });

    data = response.data;
  } catch (error) {
    if (ctx.res) {
      ctx.res.statusCode = (error as any).response.status;
    }
  }

  if (ctx.res && data && data.visibility === "ARCHIVED") {
    ctx.res.writeHead(301, {
      Location: `/retired-series`,
    });

    ctx.res.end();

    return {} as any;
  }

  const commonData = await getCommonData(ctx.req);
  const announcements = await getAnnouncements("SHOP");

  return {
    data,
    commonData,
    announcements,
  };
};
export default SerieShop;
